// End-of-day consumables record: what was used, checked, then posted.
//
// Mounted from AppMain when activeModule === 'endOfDay'.
//
// THE STATUS LINE IS THE WHOLE SCREEN
// -----------------------------------
// At every step the form says whether stock has moved yet. A supervisor
// typing figures at six in the evening needs to know that nothing has
// changed until somebody posts, and the person posting needs to know that
// this is the moment it does.
//
// WHY THE EXPECTED CLOSING UPDATES AS YOU TYPE
// --------------------------------------------
// Opening plus receipts minus used minus damaged. Seeing it move while
// entering a figure is how a typo gets caught on the spot rather than at a
// stock count three weeks later.
//
// WHY A COUNT THAT DISAGREES IS NOT AN ERROR
// -------------------------------------------
// The shelf is the fact. If it disagrees with the arithmetic, the variance
// is shown and recorded, and the ledger is left alone. Setting the balance
// to match would destroy the only evidence that something went missing.

import React, { useCallback, useEffect, useState } from 'react';
import { authedFetch } from './utils/api';
import { color, radius, space } from './ui/theme';
import { Banner, Btn, Card, Chip, ErrorBox, SkeletonCards } from './ui/kit';

async function req(url, opts) {
  const res = await authedFetch(url, opts);
  if (!res.ok) {
    let d = `Request failed (${res.status})`;
    try {
      const j = await res.json();
      d = j.detail?.message || j.detail?.error || j.detail || d;
    } catch { /* keep */ }
    throw new Error(typeof d === 'string' ? d : 'Request failed');
  }
  return res.json();
}

const inputStyle = {
  width: '100%', padding: '7px 9px', borderRadius: radius.sm, fontSize: 13,
  border: `1px solid ${color.borderStrong}`, fontFamily: 'inherit',
  boxSizing: 'border-box', background: '#fff', color: color.text,
  textAlign: 'right',
};

const num = (v, dp = 2) =>
  v === null || v === undefined || v === ''
    ? '—'
    : Number(v).toLocaleString(undefined, { maximumFractionDigits: dp });

function th(align = 'left') {
  return {
    padding: space(1), textAlign: align, fontWeight: 600,
    borderBottom: `1px solid ${color.border}`, whiteSpace: 'nowrap',
  };
}
function td(align = 'left') {
  return {
    padding: space(1), textAlign: align,
    borderBottom: `1px solid ${color.border}`,
  };
}

const STATUS = {
  draft: { tone: 'neutral', moved: false, says: 'Being filled in. No stock has moved.' },
  returned: { tone: 'warning', moved: false, says: 'Sent back for correction. No stock has moved.' },
  submitted: { tone: 'info', moved: false, says: 'Waiting to be checked. No stock has moved.' },
  approved: { tone: 'info', moved: false, says: 'Checked. Posting it will deduct the stock.' },
  posted: { tone: 'success', moved: true, says: 'Posted. The stock movements exist.' },
  reversed: { tone: 'danger', moved: true, says: 'Reversed. The movements and their mirror both stand.' },
};

// ---------------------------------------------------------------------------

function RecordForm({ recordId, onClose, notify }) {
  const [data, setData] = useState(null);
  const [draft, setDraft] = useState({});
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setErr('');
    try {
      const d = await req(`/api/consumption/${recordId}`);
      setData(d);
      const seed = {};
      d.lines.forEach((l) => {
        seed[l.line_id] = {
          used: String(l.quantity_consumed ?? 0),
          damaged: String(l.quantity_damaged ?? 0),
          reason: l.damage_reason || '',
          counted: l.physical_closing === null || l.physical_closing === undefined
            ? '' : String(l.physical_closing),
        };
      });
      setDraft(seed);
    } catch (e) { setErr(e.message); }
  }, [recordId]);

  useEffect(() => { load(); }, [load]);

  if (err) return <ErrorBox msg={err} />;
  if (!data) return <SkeletonCards n={3} />;

  const rec = data.record;
  const st = STATUS[rec.status] || STATUS.draft;
  const editable = rec.can_edit;

  const set = (id, field, value) =>
    setDraft({ ...draft, [id]: { ...draft[id], [field]: value } });

  const call = async (path, body, label) => {
    setBusy(true);
    try {
      const r = await req(`/api/consumption/${recordId}/${path}`, {
        method: path === 'lines' ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body || {}),
      });
      notify?.(r.detail || `${label} done`, r.already ? 'info' : 'success');
      await load();
    } catch (e) {
      // A shortage comes back structured; show the first item rather than
      // "[object Object]".
      notify?.(e.message, 'error');
    } finally { setBusy(false); }
  };

  const save = () => call('lines', {
    lines: data.lines.map((l) => {
      const d = draft[l.line_id] || {};
      return {
        line_id: l.line_id,
        quantity_consumed: Number(d.used || 0),
        quantity_damaged: Number(d.damaged || 0),
        damage_reason: d.reason || null,
        physical_closing: d.counted === '' ? null : Number(d.counted),
      };
    }),
  }, 'Saved');

  const askAnd = (path, prompt, label) => {
    const reason = window.prompt(prompt, '');
    if (reason === null) return;
    if (reason.trim().length < 3) {
      notify?.('A reason of at least 3 characters is needed', 'error');
      return;
    }
    call(path, { reason: reason.trim() }, label);
  };

  return (
    <Card>
      <div style={{
        display: 'flex', justifyContent: 'space-between', gap: space(1),
        flexWrap: 'wrap', alignItems: 'flex-start',
      }}>
        <div>
          <div style={{ display: 'flex', gap: space(1), alignItems: 'center', flexWrap: 'wrap' }}>
            <span style={{ fontWeight: 650, fontSize: 15 }}>{rec.record_number}</span>
            <Chip tone={st.tone}>{rec.status}</Chip>
          </div>
          <div style={{ fontSize: 12.5, color: color.textSecondary, marginTop: 3 }}>
            {rec.warehouse_name} · {String(rec.record_date).slice(0, 10)}
            {rec.shift ? ` · ${rec.shift}` : ''}
            {rec.recorded_by_name ? ` · recorded by ${rec.recorded_by_name}` : ''}
            {rec.verified_by_name ? ` · checked by ${rec.verified_by_name}` : ''}
          </div>
        </div>
        <Btn size="sm" variant="secondary" onClick={onClose}>Back</Btn>
      </div>

      <div style={{ marginTop: space(1.5) }}>
        <Banner tone={st.moved ? 'success' : 'info'}
                title={st.moved ? 'Stock has moved' : 'No stock has moved yet'}>
          {st.says}
          {rec.status === 'returned' && rec.return_reason
            ? ` — ${rec.return_reason}` : ''}
        </Banner>
      </div>

      <div style={{ overflowX: 'auto', marginTop: space(1.5) }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ background: color.bg }}>
              <th style={th()}>Item</th>
              <th style={th()}>Unit</th>
              <th style={th('right')}>Opening</th>
              <th style={th('right')}>Received</th>
              <th style={th('right')}>Used</th>
              <th style={th('right')}>Damaged</th>
              <th style={th()}>Why damaged</th>
              <th style={th('right')}>Expected</th>
              <th style={th('right')}>Counted</th>
              <th style={th('right')}>Variance</th>
            </tr>
          </thead>
          <tbody>
            {data.lines.map((l) => {
              const d = draft[l.line_id] || {};
              const used = Number(d.used || 0);
              const damaged = Number(d.damaged || 0);
              const expected = Number(l.opening_balance) + Number(l.receipts)
                - used - damaged;
              const counted = d.counted === '' ? null : Number(d.counted);
              const variance = counted === null ? null : counted - expected;
              const off = variance !== null && Math.abs(variance) > 1e-9;
              return (
                <tr key={l.line_id}>
                  <td style={td()}>{l.name}</td>
                  <td style={{ ...td(), color: color.textSecondary }}>{l.unit || '—'}</td>
                  <td style={td('right')}>{num(l.opening_balance)}</td>
                  <td style={{ ...td('right'), color: l.receipts > 0 ? color.success : color.textMuted }}>
                    {l.receipts > 0 ? num(l.receipts) : '—'}
                  </td>
                  <td style={{ ...td('right'), width: 110 }}>
                    {editable ? (
                      <input style={inputStyle} inputMode="decimal"
                             value={d.used ?? ''}
                             onChange={(e) => set(l.line_id, 'used', e.target.value)} />
                    ) : num(l.quantity_consumed)}
                  </td>
                  <td style={{ ...td('right'), width: 110 }}>
                    {editable ? (
                      <input style={inputStyle} inputMode="decimal"
                             value={d.damaged ?? ''}
                             onChange={(e) => set(l.line_id, 'damaged', e.target.value)} />
                    ) : num(l.quantity_damaged)}
                  </td>
                  <td style={{ ...td(), minWidth: 160 }}>
                    {editable ? (
                      <input style={{ ...inputStyle, textAlign: 'left' }}
                             value={d.reason ?? ''}
                             placeholder={damaged > 0 ? 'required' : ''}
                             onChange={(e) => set(l.line_id, 'reason', e.target.value)} />
                    ) : (l.damage_reason || '—')}
                  </td>
                  <td style={{ ...td('right'), fontWeight: 650 }}>{num(expected)}</td>
                  <td style={{ ...td('right'), width: 110 }}>
                    {editable ? (
                      <input style={inputStyle} inputMode="decimal"
                             value={d.counted ?? ''}
                             onChange={(e) => set(l.line_id, 'counted', e.target.value)} />
                    ) : num(l.physical_closing)}
                  </td>
                  <td style={{
                    ...td('right'), fontWeight: off ? 650 : 400,
                    color: off ? (variance < 0 ? color.danger : color.warning)
                      : color.textMuted,
                  }}>
                    {variance === null ? '—'
                      : off ? (variance > 0 ? '+' : '') + num(variance) : '0'}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div style={{ marginTop: space(1), fontSize: 12, color: color.textMuted }}>
        Received is read from the stock ledger — whoever took the delivery
        already posted it, so this record never posts it again. A count that
        disagrees with Expected is recorded as a variance; the balance is
        left alone.
      </div>

      <div style={{ marginTop: space(2), display: 'flex', gap: space(1), flexWrap: 'wrap' }}>
        {editable && <Btn onClick={save} disabled={busy}>Save</Btn>}
        {editable && (
          <Btn variant="secondary" disabled={busy}
               onClick={() => call('submit', {}, 'Submitted')}>
            Submit for checking
          </Btn>
        )}
        {rec.status === 'submitted' && (
          <>
            <Btn onClick={() => call('approve', {}, 'Approved')} disabled={busy}>
              Check &amp; approve
            </Btn>
            <Btn variant="secondary" disabled={busy}
                 onClick={() => askAnd('return', 'What needs correcting?', 'Returned')}>
              Send back
            </Btn>
          </>
        )}
        {rec.status === 'approved' && (
          <Btn onClick={() => call('post', {}, 'Posted')} disabled={busy}>
            Post &amp; deduct stock
          </Btn>
        )}
        {rec.status === 'posted' && (
          <Btn variant="secondary" disabled={busy}
               onClick={() => askAnd('reverse',
                 'Why is this being reversed? The original movements are kept and a mirror is posted.',
                 'Reversed')}>
            Reverse
          </Btn>
        )}
      </div>

      {(data.events || []).length > 0 && (
        <details style={{ marginTop: space(2) }}>
          <summary style={{ cursor: 'pointer', fontSize: 12.5, color: color.textSecondary }}>
            History ({data.events.length})
          </summary>
          <div style={{ marginTop: space(1) }}>
            {data.events.map((e, i) => (
              <div key={i} style={{ fontSize: 12, color: color.textSecondary, padding: '3px 0' }}>
                <strong>{e.event}</strong>
                {e.actor_name ? ` by ${e.actor_name}` : ''}
                {e.detail ? ` — ${e.detail}` : ''}
                {e.reason ? ` (${e.reason})` : ''}
              </div>
            ))}
          </div>
        </details>
      )}
    </Card>
  );
}

// ---------------------------------------------------------------------------

export default function EndOfDayDesk({ notify }) {
  const [rows, setRows] = useState(null);
  const [warehouses, setWarehouses] = useState([]);
  const [warehouseId, setWarehouseId] = useState('');
  const [open, setOpen] = useState(null);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setErr('');
    try {
      const [r, w] = await Promise.all([
        req('/api/consumption'),
        req('/api/warehouses/?size=200').catch(() => ({ items: [] })),
      ]);
      setRows(r);
      const list = Array.isArray(w) ? w : (w.items || []);
      const mapped = list.map((x) => ({ id: x.id, name: x.name }))
                         .filter((x) => x.id);
      setWarehouses(mapped);
      if (!warehouseId && mapped.length) {
        const factory = mapped.find((x) => /factory/i.test(x.name));
        setWarehouseId((factory || mapped[0]).id);
      }
    } catch (e) { setErr(e.message); }
  }, [warehouseId]);

  useEffect(() => { load(); }, [load]);

  const start = async () => {
    if (!warehouseId) { notify?.('Pick a store', 'error'); return; }
    setBusy(true);
    try {
      const r = await req('/api/consumption/open', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ warehouse_id: warehouseId }),
      });
      notify?.(r.detail, r.reused ? 'info' : 'success');
      setOpen(r.record_id);
      await load();
    } catch (e) { notify?.(e.message, 'error'); }
    finally { setBusy(false); }
  };

  if (err) return <ErrorBox msg={err} />;
  if (!rows) return <SkeletonCards n={3} />;

  if (open) {
    return (
      <RecordForm recordId={open} notify={notify}
                  onClose={() => { setOpen(null); load(); }} />
    );
  }

  const unposted = rows.filter(
    (r) => !['posted', 'reversed'].includes(r.status));

  return (
    <div>
      {unposted.length > 0 && (
        <div style={{ marginBottom: space(2) }}>
          <Banner tone="warning" title={`${unposted.length} record${unposted.length === 1 ? '' : 's'} not yet posted`}>
            Nothing has been deducted for these days. Consumption that is
            never posted leaves the ledger showing stock the factory has
            already used.
          </Banner>
        </div>
      )}

      <div style={{
        display: 'flex', justifyContent: 'space-between', gap: space(1),
        flexWrap: 'wrap', marginBottom: space(2), alignItems: 'flex-end',
      }}>
        <label style={{ minWidth: 220 }}>
          <div style={{ fontSize: 12.5, color: color.textSecondary, marginBottom: 4 }}>
            Store
          </div>
          <select style={{ ...inputStyle, textAlign: 'left' }} value={warehouseId}
                  onChange={(e) => setWarehouseId(e.target.value)}>
            {warehouses.map((w) => (
              <option key={w.id} value={w.id}>{w.name}</option>
            ))}
          </select>
        </label>
        <Btn onClick={start} disabled={busy}>Start today&apos;s record</Btn>
      </div>

      <Card pad={0}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: color.bg }}>
                <th style={th()}>Record</th>
                <th style={th()}>Date</th>
                <th style={th()}>Store</th>
                <th style={th()}>Status</th>
                <th style={th('right')}>Items</th>
                <th style={th('right')}>Used</th>
                <th style={th('right')}>Damaged</th>
                <th style={th()}>Recorded by</th>
                <th style={th()}></th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr><td style={td()} colSpan={9}>
                  No records yet. Start one and every tracked consumable is
                  loaded with its opening balance.
                </td></tr>
              )}
              {rows.map((r) => {
                const st = STATUS[r.status] || STATUS.draft;
                return (
                  <tr key={r.id}>
                    <td style={td()}>{r.record_number}</td>
                    <td style={td()}>{String(r.record_date).slice(0, 10)}</td>
                    <td style={{ ...td(), color: color.textSecondary }}>{r.warehouse_name}</td>
                    <td style={td()}><Chip tone={st.tone}>{r.status}</Chip></td>
                    <td style={td('right')}>{r.line_count}</td>
                    <td style={td('right')}>{num(r.total_consumed)}</td>
                    <td style={{ ...td('right'), color: r.total_damaged > 0 ? color.danger : color.textMuted }}>
                      {r.total_damaged > 0 ? num(r.total_damaged) : '—'}
                    </td>
                    <td style={{ ...td(), color: color.textSecondary }}>{r.recorded_by_name || '—'}</td>
                    <td style={td()}>
                      <Btn size="sm" variant="secondary" onClick={() => setOpen(r.id)}>
                        Open
                      </Btn>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
